import React from 'react';
import { ArrowRight, FileText, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BackgroundPattern } from './BackgroundPattern';
import { useLanguage, Language } from '../contexts/LanguageContext';

// El botón principal lleva directo al formulario de pedido con el paquete de 1 persona ($99),
// igual que el botón "Iniciar solicitud" del encabezado.
const START_TO = '/pedido?plan=solo';
type HeroStrings = {
  eyebrow: string;
  titleLead: string;
  titleAccent: string;
  subtitle1: string;
  subtitleMid: string;
  subtitle3: string;
  ctaStart: string;
  ctaHow: string;
  ctaGuide: string;
  trust1: string;
  trust2: string;
  trust3: string;
};
// EN/ES finalized. HT/VI = best effort, pending native review.
const T: Record<Language, HeroStrings> = {
  en: {
    eyebrow: 'U.S. Passport Form Preparation',
    titleLead: 'We Prepare. You Submit.',
    titleAccent: 'Simple.',
    subtitle1: 'Every U.S. passport form',
    subtitleMid: '— not just the DS-11 —',
    subtitle3: 'prepared in your language and ready to submit yourself.',
    ctaStart: 'Start Your Forms',
    ctaHow: 'See How It Works',
    ctaGuide: 'Not sure which form you need?',
    trust1: 'Multilingual Support',
    trust2: 'Free Digital Delivery',
    trust3: 'Optional Printed Delivery'
  },
  es: {
    eyebrow: 'Preparación de formularios de pasaporte de EE. UU.',
    titleLead: 'Nosotros preparamos. Usted envía.',
    titleAccent: 'Fácil.',
    subtitle1: 'Cualquier formulario de pasaporte de EE. UU.',
    subtitleMid: '— no solo el DS-11 —',
    subtitle3: 'preparado en su idioma y listo para que usted lo entregue.',
    ctaStart: 'Comenzar sus formularios',
    ctaHow: 'Ver cómo funciona',
    ctaGuide: '¿No sabe qué formulario necesita?',
    trust1: 'Atención en varios idiomas',
    trust2: 'Entrega digital gratis',
    trust3: 'Entrega impresa opcional'
  },
  ht: {
    eyebrow: 'Preparasyon fòm paspò ameriken',
    titleLead: 'Nou prepare. Ou soumèt.',
    titleAccent: 'Senp.',
    subtitle1: 'Nenpòt fòm paspò ameriken',
    subtitleMid: '— pa sèlman DS-11 la —',
    subtitle3: 'prepare nan lang ou epi pare pou ou soumèt li tèt ou.',
    ctaStart: 'Kòmanse fòm ou yo',
    ctaHow: 'Gade kijan li mache',
    ctaGuide: 'Ou pa konnen ki fòm ou bezwen?',
    trust1: 'Sipò nan plizyè lang',
    trust2: 'Livrezon dijital gratis',
    trust3: 'Livrezon enprime opsyònèl'
  },
  vi: {
    eyebrow: 'Chuẩn bị mẫu đơn hộ chiếu Hoa Kỳ',
    titleLead: 'Chúng tôi chuẩn bị. Quý vị nộp.',
    titleAccent: 'Đơn giản.',
    subtitle1: 'Mọi mẫu đơn hộ chiếu Hoa Kỳ',
    subtitleMid: '— không chỉ DS-11 —',
    subtitle3: 'được chuẩn bị bằng ngôn ngữ của quý vị và sẵn sàng để quý vị tự nộp.',
    ctaStart: 'Bắt đầu mẫu đơn của quý vị',
    ctaHow: 'Xem cách hoạt động',
    ctaGuide: 'Quý vị chưa biết cần mẫu đơn nào?',
    trust1: 'Hỗ trợ đa ngôn ngữ',
    trust2: 'Giao bản kỹ thuật số miễn phí',
    trust3: 'Tùy chọn giao bản in'
  }
};
function TrustItem({
  label
}: {
  label: string;
}) {
  return <div className="flex items-center gap-2" data-id="element-259" data-mp="1xavxukzeea7s">
      <svg className="w-5 h-5 text-brand-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-260" data-mp="21amwtbz2vwne">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" data-id="element-261" data-mp="2697mwikm8zy4" />
      </svg>
      {label}
    </div>;
}
export function Hero() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  return <section className="relative pt-20 pb-32 lg:pt-32 lg:pb-40 overflow-hidden" data-id="element-262" data-mp="2kxubzc01p62j">
      <BackgroundPattern data-id="element-263" data-mp="2ozz4g7erbwnl" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-264" data-mp="2p1scnr50qoxp">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-border shadow-sm mb-8" data-id="element-265" data-mp="2tyipm5lj14ew">
          <span className="flex h-2 w-2 rounded-full bg-brand-accent" data-id="element-266" data-mp="uoak1pprb1vt"></span>
          <span className="text-sm font-medium text-brand-textPrimary" data-id="element-267" data-mp="zmc4of2tz99e">
            {t.eyebrow}
          </span>
        </div>

        <h1 className="max-w-5xl mx-auto text-5xl md:text-6xl lg:text-7xl font-bold text-brand-textPrimary tracking-tight mb-6 text-balance leading-[1.05]" data-id="element-268" data-mp="4dgywy8mht9p">
          {t.titleLead} <span className="text-brand-accent" data-id="element-269" data-mp="e88xa4a14k5j">{t.titleAccent}</span>
        </h1>

        <p className="max-w-2xl mx-auto text-xl md:text-2xl text-brand-textSecondary mb-12 leading-relaxed" data-id="element-270" data-mp="9a7dfh4s4gt0">
          <span className="block" data-id="element-271" data-mp="3hrbu7ltckhwf">{t.subtitle1}</span>
          <span className="block" data-id="element-272" data-mp="3cr7g22fwvz9d">{t.subtitleMid}</span>
          <span className="block" data-id="element-273" data-mp="3csqvxrxno8am">{t.subtitle3}</span>
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-274" data-mp="3rglrdg31sqre">
          <Link to={START_TO} className="w-full sm:w-auto flex items-center justify-center gap-2 bg-brand-accent hover:bg-brand-accentHover text-brand-textPrimary px-8 py-4 rounded-full font-semibold text-lg transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 focus:ring-offset-brand-bg group" data-id="element-275" data-mp="3mpn57zvdoyg8">
            {t.ctaStart}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-276" data-mp="1e9ned3iqn30j" />
          </Link>

          <Link to="/how-it-works" className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-brand-textPrimary border border-brand-border px-8 py-4 rounded-full font-medium text-lg transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-textPrimary focus:ring-offset-2 focus:ring-offset-brand-bg" data-id="element-277" data-mp="zliye7b2l5lg">
            <FileText className="w-5 h-5 text-brand-textSecondary" data-id="element-278" data-mp="1j7f7g0v4cv6b" />
            {t.ctaHow}
          </Link>
        </div>

        <div className="mt-6" data-id="element-279" data-mp="1n8ha6ro8vpd1">
          <Link to="/guia" className="inline-flex items-center gap-2 text-lg font-medium text-brand-textPrimary underline underline-offset-4 hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 rounded" data-id="element-280" data-mp="1nbwr801hb8h3">
            <HelpCircle className="w-5 h-5" data-id="element-281" data-mp="2egsn698777q2" />
            {t.ctaGuide}
          </Link>
        </div>

        <div className="mt-14 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-base font-medium text-brand-textSecondary" data-id="element-282" data-mp="29kkad2hh3qm2">
          <TrustItem label={t.trust1} data-id="element-283" data-mp="1zt7iiavod0g1" />
          <TrustItem label={t.trust2} data-id="element-284" data-mp="1zs4rm2ycqy04" />
          <TrustItem label={t.trust3} data-id="element-285" data-mp="1vr2pned1w7ug" />
        </div>
      </div>
    </section>;
}